import React, { useContext, useEffect, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { FunnelIcon, Bars3BottomLeftIcon } from '@heroicons/react/24/solid'
import { isEmpty } from 'lodash'
import MultiSelect, { SelectDataType } from '../atoms/Select/Multi'
import { ActionType, CompaniesContext } from '@/context/companiesContext'
import { skillsGeneral } from '@/data/jobData'
import styles from './CompaniesFilters.module.scss'
import { sortingCompanies } from '@/data/sortingOptions'
import { CompanyFilters, CompanyFiltersRwdWrapper } from '.'

interface iProps {
  locations: Array<object>
  slugState?: any
  handleOrderChange: any
  totalCount: number
}

const CompaniesFilters = ({
  locations,
  slugState,
  handleOrderChange,
  totalCount,
}: iProps) => {
  const { state, dispatch } = useContext(CompaniesContext)
  const [companyName, setCompanyName] = useState(slugState?.filters?.company)
  const [isVisible, setIsVisible] = useState(false)
  const [isVisibleOrder, setIsVisibleOrder] = useState(false)
  const [typing, setTyping] = useState(false)
  const { t } = useTranslation('common')

  const handleSelectChange = (value: SelectDataType | null, key: string) => {
    dispatch({
      type: ActionType.ChangeFilter,
      payload: { ...state.filters, [key]: value },
    })
  }

  const handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget
    setTyping(true)
    setCompanyName(value)
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (typing) {
        dispatch({
          type: ActionType.ChangeFilter,
          payload: { ...state.filters, company: companyName },
        })
        setTyping(false)
      }
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [companyName])

  useEffect(() => {
    if (!typing) {
      setCompanyName(state.filters?.company)
    }
  }, [state])

  const handleVisibleChange = (e: any) => {
    e.preventDefault()
    setIsVisible(!isVisible)
  }

  const onOrderChange = (value: SelectDataType | null) => {
    handleOrderChange(value, 'order')
  }

  const handleVisibleOrder = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setIsVisibleOrder(!isVisibleOrder)
  }

  const handleClearFilters = (e: any) => {
    dispatch({
      type: ActionType.ChangeFilter,
      payload: { company: '', city: [], skills: [] },
    })
  }

  const handleFilterSubmit = (e: any) => {
    e.preventDefault()
  }
  const inputClassname = (option: { value: string; label: string }) => {
    let output = ''

    if (state.order?.label === option.label) {
      output = 'active'
    }

    if (isEmpty(state.order) && option.label === 'default') {
      output = 'active'
    }

    return output
  }

  return (
    <>
      <div className='container mb-12 md:hidden'>
        <div className='flex justify-between'>
          <button
            className={`border border-gray-300 md:hidden rounded-full px-8 py-4 font-xl hover:border-black ${styles.filters__filterMobileButton}`}
            onClick={handleVisibleChange}
            type='button'
          >
            <span className='icon'>
              <FunnelIcon />
            </span>
            <span>{t('common:filter')}</span>
          </button>

          <button
            className={`border border-gray-300 md:hidden rounded-full px-8 py-4 font-xl hover:border-black ${styles.filters__filterMobileButton}`}
            onClick={() => setIsVisibleOrder(true)}
            type='button'
          >
            <span className='icon'>
              <Bars3BottomLeftIcon />
            </span>
            <span>{t('common:sort')}</span>
          </button>
        </div>
      </div>
      <div
        className={`flex justify-between items-center px-5 md:px-0 pb-5 md:pb-0 md:hidden ${styles['filters__wrapper-rwd']} `}
      >
        <button
          className={`border border-gray-300 md:hidden rounded-full px-8 py-4 font-xl hover:border-black ${styles.filters__filterMobileButton}`}
          onClick={handleVisibleChange}
          type='button'
        >
          <span className='icon'>
            <FunnelIcon />
          </span>
          <span>{t('common:filter')}</span>
        </button>

        <button
          className={`border border-gray-300 md:hidden rounded-full px-8 py-4 font-xl hover:border-black ${styles.filters__filterMobileButton}`}
          onClick={() => setIsVisibleOrder(true)}
          type='button'
        >
          <span className='icon'>
            <Bars3BottomLeftIcon />
          </span>
          <span>{t('common:sort')}</span>
        </button>
      </div>
      <CompanyFilters
        handleVisibleChange={handleVisibleOrder}
        isVisible={isVisibleOrder}
        className='md:hidden'
      >
        <div className={`${styles.filters__sortingCompanies}`}>
          {sortingCompanies.map((option) => (
            <button
              type='button'
              onClick={() => onOrderChange(option)}
              key={option.label}
            >
              {t(option.label)}
              <span className={inputClassname(option)} />
            </button>
          ))}
        </div>
      </CompanyFilters>
      <CompanyFiltersRwdWrapper
        handleVisibleChange={handleVisibleChange}
        isVisible={isVisible}
      >
        <div className={`${styles.filters__form}`}>
          <div
            className='fixed bottom-0 right-0 py-3 bg-white border-t border-gray-100 z-10 px-5 md:hidden'
            style={{ width: 'calc(100% - 44px' }}
          >
            <button
              onClick={handleVisibleChange}
              type='button'
              className='bg-black rounded-full py-4 block w-full font-normal text-xl  text-white  '
            >
              {t('common:show_profiles')}{' '}
              <span className='text-gray-400'>({totalCount})</span>
            </button>
          </div>
          <header className='md:hidden flex justify-between'>
            <h2 className='font-medium text-2xl'>{t('common:filter')}</h2>
            <button
              type='button'
              className='bg-black text-white rounded-lg px-3 py-1 text-sm tracking-wide'
              onClick={handleClearFilters}
            >
              {t('common:clear_all')}
            </button>
          </header>
          <form onSubmit={handleFilterSubmit}>
            <div className='flex flex-wrap justify-between'>
              <div className='md:flex-1 w-full md:mr-9 '>
                <input
                  type='text'
                  name='company'
                  className='text-2xl font-medium w-full h-full transition duration-300 border-b md:border bg-white placeholder-black py-6 md:py-1 md:px-5 flex items-center justify-between border-gray-100  md:border-gray-dark md:rounded-lg'
                  onChange={handleInputChange}
                  placeholder={t('common:company_name')}
                  value={companyName}
                />
              </div>
              <MultiSelect
                state={state}
                onSelect={handleSelectChange}
                name='city'
                value={slugState?.filters?.city}
                data={locations}
                title={t('common:localization')}
                className={`md:flex-1 select--rwd md:mr-9 ${styles.filters__select}`}
              />
              <MultiSelect
                state={state}
                onSelect={handleSelectChange}
                name='skills'
                value={slugState?.filters?.skills}
                data={skillsGeneral}
                title={t('common:technology')}
                className={`md:flex-1 select--rwd ${styles.filters__select}`}
              />
            </div>
            <div className='mb-5' />
          </form>
        </div>
      </CompanyFiltersRwdWrapper>
    </>
  )
}

export default CompaniesFilters
